import { type Position } from "../types";

// Generic localStorage utilities with error handling
export const safeLocalStorage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Silently ignore storage errors
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Silently ignore storage errors
    }
  },
};

// Session storage utilities for temporary data
export const safeSessionStorage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Silently ignore storage errors
    }
  },
};

// Position-specific storage helpers
export const positionStorage = {
  saveIconPosition: (pos: Position): void => {
    safeLocalStorage.set("perf-dashboard-icon-pos", pos);
  },

  loadIconPosition: (defaultPos: Position): Position => {
    return safeLocalStorage.get("perf-dashboard-icon-pos", defaultPos);
  },

  saveExpandedPosition: (pos: Position): void => {
    safeLocalStorage.set("perf-dashboard-expanded-pos", pos);
  },

  loadExpandedPosition: (defaultPos: Position): Position => {
    return safeLocalStorage.get("perf-dashboard-expanded-pos", defaultPos);
  },

  saveMinimizedState: (minimized: boolean): void => {
    safeLocalStorage.set("perf-dashboard-minimized", minimized);
  },

  loadMinimizedState: (defaultValue: boolean = true): boolean => {
    return safeLocalStorage.get("perf-dashboard-minimized", defaultValue);
  },
};

// Queue data storage helpers
export const queueStorage = {
  saveSnapshot: (data: unknown): void => {
    safeSessionStorage.set("perf-dashboard-last", data);
  },

  loadSnapshot: (): unknown[] | null => {
    return safeSessionStorage.get("perf-dashboard-last", null);
  },
};
